import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import A from '@adsk/bim360-matrix-react-a';
import './_Tabs.scss';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

/**
 * Tabs component
 */
const Tabs = props => {
  const classes = classNames('Tabs', props.className, {
    [`Tabs--${props.theme}`]: props.theme !== Tabs.Themes.default
  });

  const _isSelctedTab = tab => {
    return props.selectedTab && props.selectedTab.id === tab.id;
  };

  const _handleOnTabClick = (event, tab) => {
    const selectedTabClicked = _isSelctedTab(tab);
    const switchedTab = !selectedTabClicked;
    if (selectedTabClicked && !props.canClickSelectedTab) {
      // ctrl+click or cmd+click should have default behavior of anchor link
      if (event.ctrlKey || event.metaKey) return;

      event.preventDefault();
      return;
    }

    if (tab.onClick || props.onTabChange) {
      // ctrl+click or cmd+click should have default behavior of anchor link
      if (event.ctrlKey || event.metaKey) return;

      event.preventDefault();

      if (tab.onClick) {
        tab.onClick(props.selectedTab);
      }

      if (props.onTabChange && switchedTab) {
        props.onTabChange(tab);
      }
    }
  };

  const _handleUrl = url => {
    if (typeof url === 'function') {
      return url();
    }

    return url;
  };

  return (
    <div className={classes}>
      {props.tabs.map(tab => {
        const isSelected = _isSelctedTab(tab);
        const tabClass = classNames('Tabs__tab', {
          'Tabs__tab--selected': isSelected,
          'Tabs__tab--not-clickable': isSelected && !props.canClickSelectedTab
        });

        return (
          <A
            key={tab.id}
            className={tabClass}
            onClick={event => _handleOnTabClick(event, tab)}
            href={tab.url && _handleUrl(tab.url)}
            target={tab.url && '_blank'}
          >
            <span className="Tabs__tab-name">{tab.name()}</span>
          </A>
        );
      })}
    </div>
  );
};

Tabs.Themes = {
  admin: 'admin',
  default: 'default'
};

Tabs.tabShape = PropTypes.shape({
  /**
   * A function that returns the name of the tab
   */
  name: PropTypes.func.isRequired,
  /**
   * The id of the tab (used to match with the selectedTabId)
   */
  id: PropTypes.string.isRequired,
  /**
   * Url to open on mouse middle-click / right-click => open in new tab/window
   * If no onClick is passed for this tab, then also the url to open in a new tab on left click
   */
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Optional function to run when the tab is pressed
   */
  onClick: PropTypes.func
});

Tabs.defaultProps = {
  canClickSelectedTab: true,
  theme: Tabs.Themes.default
};

Tabs.propTypes = {
  /**
   * An optional function that will be called whenever a tab is clicked in the tab navigation area
   */
  onTabChange: PropTypes.func,
  /**
   * The list of tabs in the tab navigation area
   */
  tabs: PropTypes.arrayOf(Tabs.tabShape).isRequired,
  /**
   * Whether selected tabs are clickable or not. Defaults to true
   */
  canClickSelectedTab: PropTypes.bool,
  /**
   * A custom className for the component container
   */
  className: PropTypes.string,
  /**
   * A theme name for the tab styles
   */
  theme: PropTypes.oneOf(objectValues(Tabs.Themes)),
  /**
   * The selected tab object to highlight in the tab navigation area
   */
  selectedTab: Tabs.tabShape
};

export default Tabs;
